import { useCallback, useEffect, useState } from "react";
import { admin, create as createApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Pencil, Trash2, Zap } from "lucide-react";
import { AutoApproveRuleDialog } from "@/components/AutoApproveRuleDialog";
import { AutoApproveAuditLog } from "@/components/AutoApproveAuditLog";
import { BopAutoApproveCard } from "@/components/BopAutoApproveCard";

function RuleRow({ r, onEdit, onDelete }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`auto-rule-row-${r.id}`}>
      <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
      <td className="px-3 py-2 font-mono text-xs">{r.compcode}</td>
      <td className="px-3 py-2"><span className="font-mono text-xs">{r.pcode}</span>
        {r.supplier_name && <div className="text-xs text-slate-500">{r.supplier_name}</div>}</td>
      <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(r.purc_types || []).map((t) => (
        <span key={t} className="px-2 py-0.5 text-[11px] rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200">{t}</span>
      ))}</div></td>
      <td className="px-3 py-2">
        <span className={`text-xs font-semibold ${r.active ? "text-emerald-700" : "text-slate-400"}`} data-testid={`auto-rule-status-${r.id}`}>
          {r.active ? "Active" : "Inactive"}</span>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={() => onEdit(r)} data-testid={`auto-rule-edit-${r.id}`}><Pencil size={14} /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(r)} className="text-rose-600" data-testid={`auto-rule-delete-${r.id}`}><Trash2 size={14} /></Button>
      </td>
    </tr>
  );
}

export default function AutoApproveRulesPanel() {
  const [rules, setRules] = useState([]);
  const [master, setMaster] = useState({ plants: [], purc_types: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [auditKey, setAuditKey] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r, md] = await Promise.all([admin.listAutoRules(), createApi.masterData()]);
      setRules(r.data || []);
      setMaster({ plants: md.data?.plants || [], purc_types: md.data?.purc_types || [] });
      setAuditKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const onDelete = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"? PRs matching it will go through normal L1/L2.`)) return;
    try { await admin.deleteAutoRule(r.id); toast.success("Rule deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="auto-approve-panel">
      <BopAutoApproveCard />
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-800 inline-flex items-center gap-2"><Zap size={16} className="text-cyan-700" /> Intercompany auto-approve rules</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">When the requestor approves a PR matching an active rule (plant + supplier + purchase type), L1 and L2 are stamped automatically as SYS-AUTO.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-cyan-700 hover:bg-cyan-800" data-testid="auto-rule-add-btn">
          <Plus size={14} className="mr-1" /> New rule
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        {loading ? <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <table className="w-full text-sm" data-testid="auto-rules-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
              <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Plant</th><th className="px-3 py-2">Supplier</th>
                <th className="px-3 py-2">Purchase types</th><th className="px-3 py-2">Status</th><th /></tr>
            </thead>
            <tbody>
              {rules.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500" data-testid="auto-rules-empty">No rules. All PRs follow normal L1/L2.</td></tr>}
              {rules.map((r) => <RuleRow key={r.id} r={r} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={onDelete} />)}
            </tbody>
          </table>
        )}
      </div>
      <AutoApproveAuditLog refreshKey={auditKey} />
      <AutoApproveRuleDialog open={open} onOpenChange={setOpen} existing={editing}
        plants={master.plants} purcTypeOptions={master.purc_types} onSaved={refresh} />
    </div>
  );
}
